import { DEBOUNCE_WAIT_TIME } from '@common/constants/global.constants'
import { SelectOptionType } from '@common/enums/global.enums'
import { Option } from '@common/types/global.types'
import { getCompanyLabel, getCompanyNameAndCountry, getCompanyParentLabel } from '@common/utils/company.utils'
import RadioInput from '@components/arthurBased/inputs/RadioInput'
import TextInput from '@components/arthurBased/inputs/TextInput'
import PopIn from '@components/layout/PopIn'
import { GREY_COLORS } from '@config/theme/colors'
import { debounce, Stack, styled, Typography } from '@mui/material'
import { useCompanyApi } from '@services/api/company.api'
import { useAuth } from '@services/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import CompanyCreationPopIn from './CompanyCreationPopIn'

type TraineeCompanyFormValues = {
	company: string
	query: string
}

type TraineeCompanyPopInProps = {
	isOpen: boolean
	onClose: () => void
	onConfirm: (option: Option) => void
	selectedCompany?: string
}

/**
 * Trainee company pop-in
 * @param isOpen
 * @param onClose
 * @param onSuccess
 * @param selectedCompany
 * @constructor
 */
const TraineeCompanyPopIn = (
	{
		isOpen,
		onClose,
		onConfirm,
		selectedCompany = ''
	}: TraineeCompanyPopInProps
) => {
	const { getCompanies } = useCompanyApi()
	const { formatMessage } = useIntl()
	const { isAdmin } = useAuth()
	const [query, setQuery] = useState('')
	const [isCompanyCreationPopInOpen, setIsCompanyCreationPopInOpen] = useState(false)

	const {
		control,
		handleSubmit,
		watch
	} = useForm<TraineeCompanyFormValues>({
		defaultValues: {
			company: selectedCompany,
			query: ''
		}
	})

	const watchQuery = watch('query')
	const watchCompany = watch('company')

	const { data: companies, refetch } = useQuery({
		queryKey: ['getCompanies', query],
		queryFn: () => getCompanies(query)
	})

	const debouncedSendRequest = useCallback(debounce((value: string) => setQuery(value), DEBOUNCE_WAIT_TIME), [])

	const onSubmit = () => {
		const companyDetails = companies?.find(company => `${company.id}` === watchCompany)
		onConfirm({
			type: SelectOptionType.OPTION,
			label: companyDetails ? getCompanyLabel(companyDetails, formatMessage, isAdmin) : '',
			value: companyDetails?.id || ''
		})
	}

	useEffect(() => {
		debouncedSendRequest(watchQuery)
		return debouncedSendRequest.clear
	}, [debouncedSendRequest, watchQuery])

	return (
		<PopIn
			buttons={[{
				label: formatMessage({ id: 'global.buttons.confirm' }),
				onClick: handleSubmit(onSubmit)
			}]}
			isOpen={isOpen}
			onClose={onClose}
			title={formatMessage({ id: 'dashboard.trainees.actions.addCompany.title' })}
			subtitle={formatMessage({ id: 'dashboard.trainees.actions.addCompany.subtitle' })}
		>
			<Container>
				<TextInput
					control={control}
					name="query"
					placeholder={formatMessage({ id: 'dashboard.trainees.actions.addCompany.filters.query.placeholder' })}
				/>
				<List>
					{
						companies
							? (
								<RadioInput
									control={control}
									name="company"
									options={companies.map(company => ({
										type: SelectOptionType.OPTION,
										label: getCompanyNameAndCountry(company, formatMessage),
										secondaryLabel: getCompanyParentLabel(company, formatMessage, isAdmin),
										value: company.id
									}))}
								/>
							)
							: (
								<NoData>
									<Typography>
										<FormattedMessage id="dashboard.trainees.actions.addCompany.noData" />
									</Typography>
								</NoData>
							)
					}
				</List>
				<FormattedMessage
					id="dashboard.trainees.actions.addCompany.links.createCompany"
					values={{
						a: (text) => <LinkContainer key="container">{text}</LinkContainer>,
						b: (text) => <Typography key="text" color={GREY_COLORS[500]} variant="body2">{text}</Typography>,
						c: (text) => <Link key="link" onClick={() => setIsCompanyCreationPopInOpen(true)}>{text}</Link>
					}}
				/>
			</Container>
			{isCompanyCreationPopInOpen && (
				<CompanyCreationPopIn
					onClose={() => setIsCompanyCreationPopInOpen(false)}
					onSuccess={(company) => {
						setIsCompanyCreationPopInOpen(false)
						onConfirm({
							type: SelectOptionType.OPTION,
							label: getCompanyLabel(company, formatMessage, isAdmin),
							value: company.id
						})
					}}
					isOpen
				/>
			)}
		</PopIn>
	)
}

const Container = styled(Stack)({
	minHeight: 0,
	rowGap: '20px'
})

const List = styled(Stack)({
	flex: 1,
	rowGap: '16px',
	overflow: 'auto'
})

const NoData = styled(Stack)({
	alignItems: 'center',
	padding: '20px'
})

const LinkContainer = styled(Stack)({
	alignItems: 'center',
	columnGap: '4px',
	flexDirection: 'row'
})

const Link = styled(Stack)({
	cursor: 'pointer',
	textDecoration: 'underline'
})

export default TraineeCompanyPopIn
